import { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Login } from './pages/login/Login';
import { Register } from './pages/register/Register';
import DesignSystem from './pages/desing-system/DesignSystem';
import { PrivateRoute } from './components/route/PrivateRoute';
import { observer } from 'mobx-react-lite';
import { GlobalSpinner } from './components/common-components/global/spinner/GlobalSpinner';
import Navbar from './components/navbar/Navbar';
import FilesDialog from './components/navbar/FilesDialog';
import NotificationPopup from './components/navbar/NotificationPopup';
import HospitalFiles from './pages/modules/hospital-files/HospitalFiles';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useStore } from './hooks/useStore';
import Dashboard from './pages/dashboard/Dashboard';
import UserManagement from './pages/modules/user-management/UserManagement';
import { ForgotPassword } from './pages/forgot-password/ForgotPassword';
import { ResetPassword } from './pages/reset-password/ResetPassword';
import HospitalManagement from './pages/modules/hospital-management/HospitalManagement';
import OvertimeManagement from './pages/modules/overtime/OvertimeManagement';
import ExpenseManagement from './pages/modules/expense/ExpenseManagement';
import Appointments from './pages/modules/appointments/Appointments';
import Persons from './pages/modules/persons/Persons';

const AppContent = observer(() => {
  const location = useLocation();
  const { authStore, notificationStore } = useStore();

  const isAuthPage = ['/login', '/register'].includes(location.pathname);
  const hasNavbar = !isAuthPage && authStore.isAuthenticated;

  useEffect(() => {
    if (authStore.isAuthenticated) {
      notificationStore.startSignalR();
    } else {
      notificationStore.stopSignalR();
    }
    return () => notificationStore.stopSignalR();
  }, [authStore.isAuthenticated]);

  return (
    <>
      <ToastContainer
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
        style={{ zIndex: 2147483647 }}
      />
      <GlobalSpinner />
      {hasNavbar && <Navbar />}
      {/* Global Files Dialog - Outside navbar for proper z-index */}
      {hasNavbar && <FilesDialog />}
      {hasNavbar && (
        <NotificationPopup
          isOpen={notificationStore.isPopupOpen}
          onClose={notificationStore.closePopup}
          onMarkAsRead={notificationStore.markAsRead}
          notifications={notificationStore.notifications}
          isLoading={notificationStore.isLoading}
        />
      )}
      <div className={`content ${hasNavbar ? 'with-navbar' : ''}`}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={authStore.isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
          />
          <Route
            path="/register"
            element={authStore.isAuthenticated ? <Navigate to="/dashboard" /> : <Register />}
          />

          {/* Forgot Password */}
          <Route
            path="/forgot-password"
            element={authStore.isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPassword />}
          />

          {/* Reset Password (accessible from email link) */}
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Protected Routes - Basic Authentication Required */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          {/* Admin Only Route */}
          <Route
            path="/design-system"
            element={
              <PrivateRoute requiredRoles={['Admin']}>
                <DesignSystem />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <PrivateRoute requiredRoles={['Admin']}>
                <UserManagement />
              </PrivateRoute>
            }
          />

          {/* User and Admin Route */}
          <Route
            path="/hospital-files"
            element={
              <PrivateRoute requiredRoles={['User', 'Admin']}>
                <HospitalFiles />
              </PrivateRoute>
            }
          />
          <Route
            path="/hospital-management"
            element={
              <PrivateRoute requiredRoles={['User', 'Admin']}>
                <HospitalManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/overtime-management"
            element={
              <PrivateRoute requiredRoles={['User', 'Admin']}>
                <OvertimeManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/expense-management"
            element={
              <PrivateRoute requiredRoles={['User', 'Admin']}>
                <ExpenseManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <PrivateRoute requiredRoles={['User', 'Admin']}>
                <Appointments />
              </PrivateRoute>
            }
          />
          <Route
            path="/persons"
            element={
              <PrivateRoute requiredRoles={['User', 'Admin']}>
                <Persons />
              </PrivateRoute>
            }
          />
          {/* Fallback Route */}
          <Route
            path="*"
            element={
              authStore.isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
            }
          />
        </Routes>
      </div>
    </>
  );
});

export const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default observer(App);
